import React, { useState, useEffect } from 'react';
import Layout from '../../../components/Layout';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Switch } from '../../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Plus, Edit, ChevronLeft, Search } from 'lucide-react';
import { RecordActions } from '../../../components/common/RecordActions';
import { RecordViewDialog } from '../../../components/common/RecordViewDialog';
import { useAuth } from '../../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { psAPI, companyAPI, controllingAPI } from '../../../services/api';
import { toast } from 'sonner';

const WBSProfiles = () => {
  const { canWrite } = useAuth();
  const writable = canWrite('wbsprofiles');
  const [viewItem, setViewItem] = useState(null);
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  
  const [companyCodes, setCompanyCodes] = useState([]);
  const [profitCenters, setProfitCenters] = useState([]);
  const [costCenters, setCostCenters] = useState([]);

  const [formData, setFormData] = useState({
    profile_id: '',
    name: '',
    description: '',
    is_account_assignment_element: true,
    is_planning_element: true,
    is_billing_element: false,
    company_code_id: '',
    profit_center_id: '',
    responsible_cost_center_id: '',
    settlement_rule_required: true,
    inherit_dates_from_project: true,
    allow_actual_postings: true,
    allow_commitment_postings: true,
    status: 'Active'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [profilesRes, ccRes, pcRes, costCenterRes] = await Promise.all([
        psAPI.getWBSProfiles(),
        companyAPI.getCompanyCodes(),
        controllingAPI.getProfitCenters(),
        controllingAPI.getCostCenters()
      ]);
      setProfiles(profilesRes.data);
      setCompanyCodes(ccRes.data);
      setProfitCenters(pcRes.data);
      setCostCenters(costCenterRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditMode(false);
    setSelectedProfile(null);
    setFormData({
      profile_id: '',
      name: '',
      description: '',
      is_account_assignment_element: true,
      is_planning_element: true,
      is_billing_element: false,
      company_code_id: '',
      profit_center_id: '',
      responsible_cost_center_id: '',
      settlement_rule_required: true,
      inherit_dates_from_project: true,
      allow_actual_postings: true,
      allow_commitment_postings: true,
      status: 'Active'
    });
    setShowDialog(true);
  };

  const handleEdit = (profile) => {
    setEditMode(true);
    setSelectedProfile(profile);
    setFormData({
      profile_id: profile.profile_id,
      name: profile.name,
      description: profile.description || '',
      is_account_assignment_element: profile.is_account_assignment_element,
      is_planning_element: profile.is_planning_element,
      is_billing_element: profile.is_billing_element,
      company_code_id: profile.company_code_id || '',
      profit_center_id: profile.profit_center_id || '',
      responsible_cost_center_id: profile.responsible_cost_center_id || '',
      settlement_rule_required: profile.settlement_rule_required,
      inherit_dates_from_project: profile.inherit_dates_from_project,
      allow_actual_postings: profile.allow_actual_postings,
      allow_commitment_postings: profile.allow_commitment_postings,
      status: profile.status
    });
    setShowDialog(true);
  };

  const handleSubmit = async () => {
    try {
      if (!formData.profile_id || !formData.name) {
        toast.error('Please fill in all required fields');
        return;
      }

      if (editMode) {
        await psAPI.updateWBSProfile(formData.profile_id, formData);
        toast.success('WBS Profile updated successfully');
      } else {
        await psAPI.createWBSProfile(formData);
        toast.success('WBS Profile created successfully');
      }
      
      setShowDialog(false);
      fetchData();
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error(error.response?.data?.detail || 'Failed to save profile');
    }
  };

  const filteredProfiles = profiles.filter(p => 
    p.profile_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Layout>
    <div className="p-6 space-y-6" data-testid="wbs-profiles-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/ps')}>
          <ChevronLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">WBS Profiles</h1>
          <p className="text-gray-500">Configure WBS element behavior and defaults</p>
        </div>
        {writable && (<Button onClick={handleCreate} data-testid="create-profile-btn">
          <Plus className="h-4 w-4 mr-2" />
          New Profile
        </Button>)}
      </div>

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search profiles..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Profile ID</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Account Assignment</TableHead>
                <TableHead>Planning Element</TableHead>
                <TableHead>Billing Element</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-[80px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">Loading...</TableCell>
                </TableRow>
              ) : filteredProfiles.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-gray-500">No profiles found</TableCell>
                </TableRow>
              ) : (
                filteredProfiles.map((profile) => (
                  <TableRow key={profile.profile_id}>
                    <TableCell className="font-medium">{profile.profile_id}</TableCell>
                    <TableCell>{profile.name}</TableCell>
                    <TableCell>
                      <Badge variant={profile.is_account_assignment_element ? 'default' : 'secondary'}>
                        {profile.is_account_assignment_element ? 'Yes' : 'No'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={profile.is_planning_element ? 'default' : 'secondary'}>
                        {profile.is_planning_element ? 'Yes' : 'No'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={profile.is_billing_element ? 'default' : 'secondary'}>
                        {profile.is_billing_element ? 'Yes' : 'No'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={profile.status === 'Active' ? 'default' : 'secondary'}>
                        {profile.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <RecordActions doctype="wbsprofiles" onView={() => setViewItem(profile)} onEdit={() => handleEdit(profile)} />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editMode ? 'Edit WBS Profile' : 'Create WBS Profile'}</DialogTitle>
          </DialogHeader>
          
          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="space-y-2">
              <Label>Profile ID *</Label>
              <Input
                value={formData.profile_id}
                onChange={(e) => setFormData({...formData, profile_id: e.target.value.toUpperCase()})}
                placeholder="e.g., WBS_STD"
                disabled={editMode}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="Profile name"
              />
            </div>

            <div className="col-span-2 space-y-2">
              <Label>Description</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
              />
            </div>

            <div className="space-y-2">
              <Label>Default Company Code</Label>
              <Select value={formData.company_code_id || "__none__"} onValueChange={(v) => setFormData({...formData, company_code_id: v === "__none__" ? "" : v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select company code" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None</SelectItem>
                  {companyCodes.map(cc => (
                    <SelectItem key={cc.company_code_id} value={cc.company_code_id}>
                      {cc.company_code_id} - {cc.company_code_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Default Profit Center</Label>
              <Select value={formData.profit_center_id || "__none__"} onValueChange={(v) => setFormData({...formData, profit_center_id: v === "__none__" ? "" : v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select profit center" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None</SelectItem>
                  {profitCenters.map(pc => (
                    <SelectItem key={pc.profit_center_id} value={pc.profit_center_id}>
                      {pc.profit_center_id} - {pc.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Responsible Cost Center</Label>
              <Select value={formData.responsible_cost_center_id || "__none__"} onValueChange={(v) => setFormData({...formData, responsible_cost_center_id: v === "__none__" ? "" : v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select cost center" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None</SelectItem>
                  {costCenters.map(cc => (
                    <SelectItem key={cc.cost_center_id} value={cc.cost_center_id}>
                      {cc.cost_center_id} - {cc.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={formData.status} onValueChange={(v) => setFormData({...formData, status: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="col-span-2 grid grid-cols-3 gap-4 pt-4 border-t">
              <div className="flex items-center justify-between">
                <Label className="text-sm">Account Assignment Element</Label>
                <Switch
                  checked={formData.is_account_assignment_element}
                  onCheckedChange={(v) => setFormData({...formData, is_account_assignment_element: v})}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label className="text-sm">Planning Element</Label>
                <Switch
                  checked={formData.is_planning_element}
                  onCheckedChange={(v) => setFormData({...formData, is_planning_element: v})}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label className="text-sm">Billing Element</Label>
                <Switch
                  checked={formData.is_billing_element}
                  onCheckedChange={(v) => setFormData({...formData, is_billing_element: v})}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label className="text-sm">Settlement Rule Required</Label>
                <Switch
                  checked={formData.settlement_rule_required}
                  onCheckedChange={(v) => setFormData({...formData, settlement_rule_required: v})}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label className="text-sm">Inherit Dates from Project</Label>
                <Switch
                  checked={formData.inherit_dates_from_project}
                  onCheckedChange={(v) => setFormData({...formData, inherit_dates_from_project: v})}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label className="text-sm">Allow Actual Postings</Label>
                <Switch
                  checked={formData.allow_actual_postings}
                  onCheckedChange={(v) => setFormData({...formData, allow_actual_postings: v})}
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSubmit}>{editMode ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>

      <RecordViewDialog
        open={!!viewItem}
        onOpenChange={(o) => { if (!o) setViewItem(null); }}
        title="Details"
        record={viewItem}
      />
    </Layout>
  );
};

export default WBSProfiles;
